import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  CardHeader,
  Col,
  Spinner,
  Input,
  InputGroup,
  Badge,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import AwesomePagination from "../../Components/Common/AwesomePagination";
import { toast } from "react-toastify";
import { APIClient, getLoggedinUser } from "../../helpers/api_helper";
import moment from "moment";
import FeatherIcon from "feather-icons-react";
import ModalImportCSV from "./ModalImportCSV";

const apiClient = new APIClient();

const NotasFiscais = () => {
  const [loading, setLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [termo, setTermo] = useState("");
  const [inputTermo, setInputTermo] = useState("");
  const [status, setStatus] = useState("");
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [userData, setUserData] = useState({});
  const [modalImportCSV, setModalImportCSV] = useState(false);

  const getNotasFiscais = useCallback(async () => {
    setLoading(true);
    const response = await apiClient.post(`/notas/busca`, {
      termo: termo,
      status: status || null,
      pagina: pagina,
      itens_pagina: 20,
      order_by: "updated_at",
      order: "desc",
    });

    if (response.sucesso) {
      setNotasFiscais(response.dados.data);
      setTotalPaginas(response.dados.last_page);
      setTotalItens(response.dados.total);
    } else {
      toast.error(response.mensagem);
    }
    setLoading(false);
  }, [pagina, termo, status]);

  useEffect(() => {
    if (shouldFetch) {
      const fetchData = async () => {
        await getNotasFiscais();
        setShouldFetch(false);
      };
      fetchData();
    }
  }, [shouldFetch, getNotasFiscais]);

  useEffect(() => {
    const getData = async () => {
      const user = await getLoggedinUser();
      setUserData(user);
    };
    getData();
  }, []);

  document.title = "Notas Fiscais | Painel Fort Exchange";

  const handleTermoKeyDown = (e) => {
    if (e.key === "Enter") {
      setTermo(inputTermo);
      setPagina(1);
      setShouldFetch(true);
    }
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPagina(1);
    setShouldFetch(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "solicitada":
        return <Badge color="warning">Solicitada</Badge>;
      case "entregue":
        return <Badge color="success">Entregue</Badge>;
      case "cancelada":
        return <Badge color="danger">Cancelada</Badge>;
      case "invalida":
        return <Badge color="danger">Inválida</Badge>;
      default:
        return <Badge color="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleImportSuccess = () => {
    setModalImportCSV(false);
    setPagina(1);
    setShouldFetch(true);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Notas Fiscais" pageTitle="Aplicações" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center border-0">
                  <h5 className="card-title mb-0 flex-grow-1">
                    Notas Fiscais ({totalItens})
                  </h5>
                  {userData?.data?.role === "Admin" && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setModalImportCSV(true)}
                    >
                      <FeatherIcon icon="upload" className="me-1" />
                      Importar CSV
                    </Button>
                  )}
                </CardHeader>
                <CardBody>
                  {/* Filtros */}
                  <Row className="mb-3">
                    <Col md={9}>
                      <InputGroup>
                        <div className="input-group-text">
                          <FeatherIcon icon="search" className="icon-dual" />
                        </div>
                        <Input
                          type="text"
                          placeholder="Buscar por referência ou status..."
                          value={inputTermo}
                          onChange={(e) => setInputTermo(e.target.value)}
                          onKeyDown={handleTermoKeyDown}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Input
                        type="select"
                        value={status}
                        onChange={handleStatusChange}
                      >
                        <option value="">Todos os Status</option>
                        <option value="nova">Nova</option>
                        <option value="solicitada">Solicitada</option>
                        <option value="entregue">Entregue</option>
                        <option value="cancelada">Cancelada</option>
                        <option value="invalida">Inválida</option>
                      </Input>
                    </Col>
                  </Row>

                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        padding: "20px",
                      }}
                    >
                      <Spinner height={100} width={100} />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Data</th>
                              <th>Status</th>
                              <th>Cliente</th>
                              <th>Referência</th>
                              <th>Transações</th>
                              <th>Valor</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {notasFiscais.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="text-center text-muted py-4"
                                >
                                  Nenhuma nota fiscal encontrada.
                                </td>
                              </tr>
                            ) : (
                              notasFiscais.map((item, idx) => {
                                const notaDados = JSON.parse(item.nota_dados);
                                return (
                                  <tr key={item.id || idx}>
                                    {/* ID */}
                                    <td>{item.id}</td>
                                    {/* Data */}
                                    <td>
                                      <b>
                                        {moment(item.created_at).format(
                                          "DD/MM/YYYY"
                                        )}
                                      </b>
                                      <br />
                                      <small className="text-muted">
                                        {moment(item.created_at).format(
                                          "HH:mm"
                                        )}
                                      </small>
                                    </td>

                                    {/* Status */}
                                    <td>{getStatusBadge(item.status)}</td>

                                    {/* Cliente */}
                                    <td>
                                      {item.cliente?.nome_completo ? (
                                        <div>
                                          <Link
                                            to={`/clientes/${item.user_id}`}
                                            className="text-primary"
                                          >
                                            <b>{item.cliente.nome_completo}</b>
                                          </Link>
                                          <br />
                                          <small className="text-muted">
                                            {item.cliente.cpf_cnpj}
                                          </small>
                                        </div>
                                      ) :item.nome ? (
                                        <div>
                                          <b className="text-primary">{item.nome}</b>
                                          <br />
                                          <small className="text-muted">
                                            {item.documento}
                                          </small>
                                        </div>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td>{item.nota_reference}</td>
                                    <td>
                                      <div>
                                        <small className="text-muted">
                                          {item.data_inicio ===
                                          item.data_fim ? (
                                            <>
                                              Compra realizada em{" "}
                                              {moment(item.data_inicio).format(
                                                "DD/MM/YYYY"
                                              )}
                                            </>
                                          ) : (
                                            <>
                                              Compras desde{" "}
                                              {moment(item.data_inicio).format(
                                                "DD/MM/YYYY"
                                              )}
                                              <br />
                                              Até:{" "}
                                              {moment(item.data_fim).format(
                                                "DD/MM/YYYY"
                                              )}
                                            </>
                                          )}
                                        </small>
                                      </div>
                                    </td>
                                    <td>
                                      {notaDados?.valor ? (
                                        <span className="fw-bold text-success">
                                          {formatCurrency(notaDados.valor)}
                                        </span>
                                      ) : (
                                        "-"
                                      )}
                                    </td>

                                    {/* Ações */}
                                    <td>
                                      {item.nota_url ? (
                                        <Link
                                          to={item.nota_url}
                                          target="_blank"
                                          className="btn btn-sm btn-primary"
                                        >
                                          <FeatherIcon
                                            icon="external-link"
                                            className="me-1"
                                          />
                                          Ver Nota
                                        </Link>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                      <AwesomePagination
                        pagina={pagina}
                        totalPaginas={totalPaginas}
                        setPagina={(p) => {
                          setPagina(p);
                          setShouldFetch(true);
                        }}
                      />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Import CSV Modal */}
      <ModalImportCSV
        isOpen={modalImportCSV}
        toggle={() => setModalImportCSV(!modalImportCSV)}
        onImportSuccess={handleImportSuccess}
      />
    </React.Fragment>
  );
};

export default NotasFiscais;
